import React, { useState } from 'react';
import {
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { TitleDivider } from '@stack/ui/components';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import last from 'lodash/last';

import { props } from './prop';

const ServiceMaintenance: React.FC<props> = (props) => {
  const { serviceData } = props;
  const [selectedTab, setSelectedTab] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const carData = serviceData?.map((e) => e?.name);
  const replaceHtmlEntities = (data?: (string | number)[][]) => {
    const entityMap: { [key: string]: string } = {
      '&nbsp;': ' ',
      '<b>': '',
      '</b>': '',
    };

    return data?.map((row) =>
      row.map((item) =>
        isNumber(item)
          ? item
          : item?.replace(/&nbsp;|<b>|<\/b>/g, (match) => entityMap[match])
      )
    );
  };

  const adjustRowSize = (data?: (string | number)[][]) => {
    const firstRowSize = data?.[0]?.length || 0;
    return data?.map((row) => {
      if (row.length < firstRowSize) {
        return [...row, ...Array(firstRowSize - row.length).fill('-')];
      }
      return row;
    });
  };

  const maintenanceData = adjustRowSize(
    replaceHtmlEntities(serviceData?.[selectedTab]?.tableData)
  );

  const copywriting = [
    '1. Service & maintenance to be carried out at intervals based on every mileage(km) or month that whichever comes first.',
    '2. Service & maintenance prices may vary for West Malaysia',
    '3. Service & maintenance prices may vary if there is any upgrade and changes by the carmaker.',
    '4. All prices are subject to 6% Service tax, except for Langkawi and Labuan',
    '5. The service job coverage is suggested at minimal replacement. The replacement may vary/alter based on user driving habits and car conditions.',
    '6. Prices of parts and items are subject to change without any prior notice. Any parts and services updates mentioned may subject to additional charges.',
  ];
  const total = last(maintenanceData);

  const MileageTable = () => {
    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 0,
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#31ACBF',
            width: 180,
            borderRadius: '8px',
          },
        }}
      >
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{
            '.MuiTableCell-body': {
              p: '8.5px 20px !important',
              fontSize: 14,
              border: 0,
            },
            '.MuiTableCell-head': {
              p: '8.5px 20px !important',
              textAlign: 'right',
              fontWeight: 500,
              border: 0,
              fontSize: '14px !important',
              background: '#E9ECF3',
            },
          }}
        >
          <TableHead>
            <TableRow>
              {serviceData?.[selectedTab]?.tableData?.[0]?.map((e, index) => (
                <TableCell
                  key={index}
                  align="right"
                  sx={{
                    fontWeight: 500,
                    '&:first-child': {
                      minWidth: { xs: '180px', md: '250px' },
                      maxWidth: '45vw',
                      textAlign: 'left',
                      position: 'sticky',
                      left: 0,
                      zIndex: '99 !important',
                    },
                  }}
                >
                  {e}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {maintenanceData?.slice(1, -1)?.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  ':hover': {
                    '.MuiTableCell-body': {
                      background: '#F7F7F7 !important',
                    },
                  },
                  '.MuiTableCell-body': {
                    borderBottom: rowIndex > 1 ? '1px solid #EDECED' : 'none',
                    pb:
                      rowIndex + 1 !== maintenanceData?.length && rowIndex > 0
                        ? '0px !important'
                        : undefined,
                    bgcolor: rowIndex === 0 ? '#F7F7F7' : 'white',
                  },
                  fontWeight:
                    rowIndex + 1 === maintenanceData?.length ? 500 : 400,
                }}
              >
                {row.map((i, index) => (
                  <TableCell
                    sx={{
                      textAlign: 'right',
                      '&:first-child': {
                        textAlign: 'left',
                        minWidth: { xs: '180px', md: '250px' },
                        maxWidth: '45vw',
                        position: 'sticky',
                        left: 0,
                      },
                    }}
                    key={index}
                  >
                    {rowIndex === 0
                      ? i?.toString()?.replace('.00', '')
                      : isNumber(i)
                      ? i?.toFixed(rowIndex === 0 ? 0 : 2)
                      : i?.replace('&nbsp;&nbsp; &gt; ', '>')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow
              sx={{
                bgcolor: '#F7F7F7',
              }}
            >
              {total?.map((i, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: '500 !important',
                    textAlign: 'right',
                    '&:first-child': {
                      textAlign: 'left',
                      minWidth: { xs: '180px', md: '250px' },
                      maxWidth: '45vw',
                      position: 'sticky',
                      left: 0,
                      zIndex: '99 !important',
                      bgcolor: '#F7F7F7',
                    },
                  }}
                >
                  {isNumber(i) ? i?.toFixed(2) : i}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (isEmpty(serviceData)) return null;
  return (
    <TitleDivider title="Service Maintenance">
      <Stack border={'1px solid #EDECED'}>
        <Tabs
          value={selectedTab}
          allowScrollButtonsMobile
          onChange={handleChange}
          TabIndicatorProps={{ style: { background: '#FFC800' } }}
        >
          {carData?.map((e, index) => (
            <Tab
              sx={{
                fontSize: 16,
                color: '#1E242F !important',
                fontWeight: 700,
                textTransform: 'capitalize',
                p: '20px 8px 12px 8px',
              }}
              key={index}
              label={e}
            />
          ))}
        </Tabs>
        <Stack>{MileageTable()}</Stack>
        <Stack p={5} direction={'column'}>
          {copywriting.map((e, index) => (
            <Typography fontSize={12} key={index}>
              {e}
            </Typography>
          ))}
        </Stack>
      </Stack>
    </TitleDivider>
  );
};

export default ServiceMaintenance;
